import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { toCamelCase } from '../../grid/utils';
import type { RevenueReportOptions, RevenueReportRequest, RevenueReportResult } from '../types';

type RevenueReportEnvelope<T = unknown> = {
  Code: number;
  data: T;
};

type RevenueReportOptionsQuery = {
  period?: string;
  companyId?: number | null;
};

const REVENUE_REPORT_API = '/umbraco/api/RevenueReport';

const transformResponse = <T>(response: RevenueReportEnvelope): T => {
  if (response.Code !== 1) {
    throw new Error('Failed to load revenue report.');
  }

  return toCamelCase(response.data) as T;
};

export const transformRevenueReportOptionsResponse = (
  response: RevenueReportEnvelope
): RevenueReportOptions => transformResponse(response);

export const transformRevenueReportResponse = (
  response: RevenueReportEnvelope
): RevenueReportResult => transformResponse(response);

export const revenueReportApi = createApi({
  reducerPath: 'revenueReportApi',
  baseQuery,
  endpoints: (builder) => ({
    getOptions: builder.query<RevenueReportOptions, RevenueReportOptionsQuery>({
      query: (params) => ({ url: `${REVENUE_REPORT_API}/GetOptions`, params }),
      transformResponse: transformRevenueReportOptionsResponse,
      keepUnusedDataFor: 300,
    }),
    generateReport: builder.query<RevenueReportResult, RevenueReportRequest>({
      query: (body) => ({
        url: `${REVENUE_REPORT_API}/GenerateGridReport`,
        method: 'POST',
        body,
      }),
      transformResponse: transformRevenueReportResponse,
    }),
  }),
});

export const { useGetOptionsQuery, useLazyGenerateReportQuery } = revenueReportApi;
